import React, {useEffect, useMemo, useRef} from "react";
import {TitleLayout} from "~/apps/core/ui/TitleLayout";
import {
    PassForm,
    Values,
    PassFormProps,
    serializeValues,
    deserializeValues, Type,
} from "~/apps/packages/passes/components/PassForm/PassForm";
import {useCreateAdmission} from "~/apps/packages/passes/hooks";
import {useHistory, useLocation} from "react-router-dom";
import {BrandAppBar} from "~/apps/core/ui/BrandAppBar";
import {Box, Container} from "@mui/material";
import {AdmissionCreateResult} from "./AdmissionCreateResult"
import {AppBarStepper} from "~/apps/core/ui/AppBarStepper";
import {Admission, AdmissionTypeEnum} from "~/queries";
import moment from "moment";
import {useUserAuthenticated} from "~/auth/hooks";


export function getLocationForCreateAdmission(data: Partial<Admission>, types?: Type[], step?: number) {
    if (types == null) {
        types = data.carRegNumber ? ["CAR"] : ["PEDESTRIAN"];
    }
    const maxSteps = types.includes("CAR") ? 5 : 4;
    step = Math.min(Math.max(0, step || 0), maxSteps);
    return {
        pathname: '/new',
        search: `step=${step}&maxSteps=${maxSteps}`,
        state: {
            ...serializeValues({
                ...data,
                agreement: false,
                birthDate: data?.birthDate ? moment(data.birthDate, "YYYY-MM-DD") : "",
                passportDate: data?.passportDate ? moment(data.passportDate, "YYYY-MM-DD") : "",
                passDate: data?.passDate ? moment(data.passDate, "YYYY-MM-DD") : "",
            }  as any),
            types: types,
        }
    }
}

export const CreatePage = () => {
    const history = useHistory<any>();
    const location = useLocation<any>();
    const userAuthenticated = useUserAuthenticated();

    // при повторе заявки начальные значения передаются через location.state
    const $values = useRef<Values>(location.state ? deserializeValues(location.state) : null);
    const $defaultTypes = useRef<Type[]>(location.state ? location.state.types : null);
    const [step, maxSteps] = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return [
            isNaN(Number(searchParams.get("step"))) ? null : Number(searchParams.get("step")),
            isNaN(Number(searchParams.get("maxSteps"))) ? null : Number(searchParams.get("maxSteps"))
        ];
    }, [location.search]);

    const [create, {data, loading, error}] = useCreateAdmission();

    useEffect(() => {
        if (!userAuthenticated) {
            history.go(-step);
            $values.current = null;
        }
    }, [userAuthenticated])

    // возврат на начало заполнения при обновлении страницы
    useEffect(() => {
        if (step && !$values.current) {
            history.go(-step);
        } else if (step >= 1 && $values.current) {
            history.go(-step);
        }
    }, []);

    const handleSubmit: PassFormProps["onSubmit"] = (data, types) => {
        return create({
            chiefFullName: data.chiefFullName,
            chiefPhone: data.chiefPhone,

            fullName: data.fullName,
            position: data.position,
            phone: data.phone,
            email: data.email,
            birthDate: data.birthDate ? moment(data.birthDate).format("YYYY-MM-DD") : null,
            birthPlace: data.birthPlace,
            passportSeries: data.passportSeries,
            passportNumber: data.passportNumber,
            passportDate: data.passportDate ? moment(data.passportDate).format("YYYY-MM-DD") : null,
            passportIssuer: data.passportIssuer,
            registrationAddress: data.registrationAddress,
            actualAddress: data.actualAddress,

            carRegNumber: data?.carRegNumber,
            carBrand: data?.carBrand,
            carModel: data?.carModel,
            carColor: data?.carColor,
            carType: data?.carType,

            passDate: data.passDate ? moment(data.passDate).format("YYYY-MM-DD") : null,
            companyActivityKind: data.companyActivityKind,
            paragraph: Number(data.paragraph),
            accessZone2: data.accessZone2,
            accessZone3: data.accessZone3,
            documents: data.documents,
        }, types as AdmissionTypeEnum[]);
    }

    const handleContinueClick = () => {
        if (location.state && location.state.isRepeating) {
            history.go(-(maxSteps + 2))
        } else {
            history.go(-(maxSteps + 1));
        }
    }

    const handleBrandClick = () => {
        history.push("/");
    }

    const handleStepChange: PassFormProps["onStepChange"] = (newStep, maxSteps) => {
        if (step != newStep) {
            scrollTo(0, 0);
            history.push({
                ...location,
                search: `step=${newStep}&maxSteps=${maxSteps - 1}`,
            });
        }
        history.replace({
            ...location,
            search: `step=${newStep}&maxSteps=${maxSteps - 1}`
        })
    }

    const handleChange: PassFormProps["onChange"] = (values) => {
        $values.current = values;
    }


    if (data) {
        return (
            <>
                <BrandAppBar onBrandClick={handleBrandClick}/>
                <Box marginTop="72px"/>
                <Container>
                    {/* @ts-ignore */}
                    <AdmissionCreateResult admission={data} error={error} onContinue={handleContinueClick} />
                </Container>
            </>
        )
    }
    return (
        <TitleLayout title="Новая заявка" disableGutters actions={
            maxSteps > 1 && <AppBarStepper steps={maxSteps} activeStep={step}/>
        }>
            <PassForm
                step={step}
                defaultTypes={$defaultTypes.current}
                defaultValues={$values.current}
                onStepChange={handleStepChange}
                onChange={handleChange}
                onSubmit={handleSubmit}
                loading={loading}
            />
        </TitleLayout>
    )
}
